import { memo, useCallback, useMemo } from 'react';
import { Select, ActionIcon, Tooltip } from '@mantine/core';
import { IconSortAscending, IconSortDescending } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ICON_SIZE_SM } from '@/constants/ui';
import { Z_INDEX_MOBILE_MODAL_DATEPICKER } from '@/constants/zIndex';
import styles from './SortControls.module.css';

export interface SortOption {
  readonly value: string;
  readonly labelKey: string;
}

interface SortControlsProps {
  readonly options: ReadonlyArray<SortOption>;
  readonly sortBy: string;
  readonly sortDescending: boolean;
  readonly onSortByChange: (value: string) => void;
  readonly onSortDescendingChange: (value: boolean) => void;
}

export const SortControls = memo(function SortControls({
  options,
  sortBy,
  sortDescending,
  onSortByChange,
  onSortDescendingChange,
}: SortControlsProps) {
  const { t } = useTranslation();

  const translatedOptions = useMemo(
    () => options.map((opt) => ({ value: opt.value, label: t(opt.labelKey) })),
    [options, t]
  );

  const handleSortByChange = useCallback(
    (value: string | null) => {
      if (value) {
        onSortByChange(value);
      }
    },
    [onSortByChange]
  );

  const handleToggleDirection = useCallback(() => {
    onSortDescendingChange(!sortDescending);
  }, [sortDescending, onSortDescendingChange]);

  const directionLabel = sortDescending
    ? t('sorting.descending')
    : t('sorting.ascending');

  return (
    <div className={styles.container}>
      <Select
        className={styles.sortSelect}
        label={t('sorting.sortBy')}
        data={translatedOptions}
        value={sortBy}
        onChange={handleSortByChange}
        allowDeselect={false}
        comboboxProps={{
          zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
          withinPortal: true,
        }}
      />
      <Tooltip label={directionLabel}>
        <ActionIcon
          className={styles.directionButton}
          variant="subtle"
          color="gray"
          onClick={handleToggleDirection}
          aria-label={directionLabel}
        >
          {sortDescending ? (
            <IconSortDescending size={ICON_SIZE_SM} />
          ) : (
            <IconSortAscending size={ICON_SIZE_SM} />
          )}
        </ActionIcon>
      </Tooltip>
    </div>
  );
});
